import {
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Tooltip,
  Box,
  FormLabel,
  Switch,
} from '@chakra-ui/react';
import {
  Api20,
  ColumnDependency20,
  Finance20,
  Locked20,
  Logout20,
  Password20,
  Settings24,
  User20,
  UserProfile20,
} from '@carbon/icons-react';

import { useCopyToClipboard } from 'react-use';
import React from 'react';
import { useInstitution } from '#core/Institution';
import { RequireRole } from '#core/Permissions';
import { useAuth } from '#core/Auth';
import { useEnvironment } from '#core/Environment';
import RequireClientRole from '#core/Permissions/components/RequireClientRole';

const SettingsButton: React.FC = () => {
  const {
    hasManyInstitutions,
    institution,
    apiHeaders: institutionApiHeaders,
    clearActiveInstitution,
  } = useInstitution();

  const {
    keycloak,
    token,
    apiHeaders: authApiHeaders,
    canUseElevatedPermissions,
    useElevatedPermissions,
    setUseElevatedPermissions,
    actions: { logOut },
  }: any = useAuth();
  const { apiEndpoint, baseUrl } = useEnvironment();
  const [, clipboardCopy] = useCopyToClipboard();

  const copyToClipboard = (data: string | object, message: string) => {
    clipboardCopy(
      typeof data === 'string' ? data : JSON.stringify(data, null, 2),
    );
    document.dispatchEvent(
      new CustomEvent('datapoints-dev-message', {
        detail: { message },
      }),
    );
  };
  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<Settings24 />}
        border="1px"
        bg="white"
        _hover={{ bg: 'gray.100' }}
        _active={{ bg: 'gray.200' }}
        w={128}
        minW={128}
      >
        Settings
      </MenuButton>
      <MenuList
        placement="bottom-start"
        bg="white"
        color="gray.800"
        borderColor="gray.800"
      >
        {hasManyInstitutions && (
          <MenuItem
            onClick={clearActiveInstitution}
            _hover={{ bg: 'gray.100' }}
            _active={{ bg: 'gray.200' }}
            _focus={{ bg: 'gray.200' }}
          >
            Change Institution
          </MenuItem>
        )}
        {canUseElevatedPermissions && (
          <MenuItem
            _hover={{ bg: 'gray.100' }}
            _active={{ bg: 'gray.200' }}
            _focus={{ bg: 'gray.200' }}
          >
            <Flex fontWeight="semibold" fontSize="md" alignItems="center">
              <Tooltip
                bgColor="white"
                color="grey.800"
                label="Use your THE permissions to view extra data that a user without elevated permissions might not see"
              >
                <Flex alignItems="center">
                  <Box pr={2}>
                    <Locked20 />
                  </Box>{' '}
                  <FormLabel htmlFor="use-elevated-permissions" m={0}>
                    Use elevated permissions
                  </FormLabel>
                </Flex>
              </Tooltip>
              <Switch
                ml={3}
                id="use-elevated-permissions"
                data-testid="use-elevated-permissions-switch"
                isChecked={useElevatedPermissions}
                onChange={(event) =>
                  setUseElevatedPermissions(event.target.checked)
                }
              />
            </Flex>
          </MenuItem>
        )}
        <RequireRole role="Developer">
          <MenuItem
            icon={<ColumnDependency20 />}
            _hover={{ bg: 'gray.100' }}
            _active={{ bg: 'gray.200' }}
            _focus={{ bg: 'gray.200' }}
            data-testid="open-graphiQl"
            onClick={() => {
              copyToClipboard(
                {
                  ...authApiHeaders,
                  ...institutionApiHeaders,
                },
                'Auth token copied to clipboard',
              );
              setTimeout(() => {
                window.open(`https://${apiEndpoint}/graphql`);
              }, 1000);
            }}
          >
            GraphiQL
          </MenuItem>
          <MenuItem
            icon={<Api20 />}
            onClick={() => {
              copyToClipboard(
                `Bearer ${token}`,
                'Auth token copied to clipboard',
              );
              setTimeout(() => {
                window.open(`https://${apiEndpoint}/legacy-api/docs/`);
              }, 1000);
            }}
            _hover={{ bg: 'gray.100' }}
            _active={{ bg: 'gray.200' }}
            _focus={{ bg: 'gray.200' }}
            data-testid="open-swagger"
          >
            Swagger
          </MenuItem>
          <MenuItem
            icon={<Password20 />}
            onClick={() =>
              copyToClipboard(
                `Bearer ${token}`,
                'Auth token copied to clipboard',
              )
            }
            _hover={{ bg: 'gray.100' }}
            _active={{ bg: 'gray.200' }}
            _focus={{ bg: 'gray.200' }}
            data-testid="copy-token"
          >
            Copy Token
          </MenuItem>
          <MenuItem
            icon={<Finance20 />}
            onClick={() =>
              copyToClipboard(
                institution.institutionId,
                'Institution ID copied to clipboard',
              )
            }
            _hover={{ bg: 'gray.100' }}
            _active={{ bg: 'gray.200' }}
            _focus={{ bg: 'gray.200' }}
            data-testid="copy-institution"
          >
            Copy Institution Id
          </MenuItem>
        </RequireRole>
        <RequireClientRole client="realm-management" role="impersonation">
          <MenuItem
            icon={<User20 />}
            onClick={keycloak?.accountManagement}
            _hover={{ bg: 'gray.100' }}
            _active={{ bg: 'gray.200' }}
            _focus={{ bg: 'gray.200' }}
          >
            Impersonate
          </MenuItem>
        </RequireClientRole>
        <MenuItem
          icon={<UserProfile20 />}
          onClick={keycloak?.accountManagement}
          _hover={{ bg: 'gray.100' }}
          _active={{ bg: 'gray.200' }}
          _focus={{ bg: 'gray.200' }}
        >
          My Profile
        </MenuItem>
        <MenuItem
          icon={<Logout20 />}
          onClick={() =>
            logOut({
              redirectUri: baseUrl,
            })
          }
          _hover={{ bg: 'gray.100' }}
          _active={{ bg: 'gray.200' }}
          _focus={{ bg: 'gray.200' }}
          data-testid="logout-button"
        >
          Logout
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default SettingsButton;
